import { useCallAPI } from 'hooks/useCallAPI';
import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n';
import {
  BeaconConnectionOption,
  BeaconMessageFormType
} from 'core/beaconMessage/BeaconMessage';
import moment from 'moment';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { BeaconType } from 'core/advertiser/BeaconConnection';
import {
  ChannelGroupManager,
  DefaultChannelGroupManager
} from 'core/channelGroup/ChannelGroupManager';
import {
  BeaconMessageManager,
  DefaultBeaconMessageManager
} from 'core/beaconMessage/BeaconMessageManager';
import {
  LineMessageFormData,
  LineMessageType
} from 'core/advertiser/LineBeacon';
import { cloneDeep } from 'lodash';
import { useParams } from 'react-router-dom';
import {
  AdvertiserManager,
  DefaultAdvertiserManager
} from 'core/advertiser/AdvertiserManager';
import {
  AdRequestSourceManager,
  DefaultAdRequestSourceManager
} from 'core/adRequestSource/AdRequestSourceManager';
// import { DefaultEditLimitationModel } from 'containers/Limitations/EditLimitationModel';
// import { useLimitationModel } from './hooks/useLimitationModel';

export type BeaconMessageSetupFlowModel = {
  type: 'create' | 'edit' | 'summary';
  defaultBeaconMessage?: BeaconMessageFormType;
  beaconMessage?: BeaconMessageFormType;
  title: string;
  loading: boolean;
  redirectPath?: string;
  readonly: boolean;
  beaconConnectionOptions: BeaconConnectionOption[];
  // limitationModel: DefaultEditLimitationModel | undefined;
  availableChannels?: SelectOptions[];
  setBeaconMessage: (beaconMessage: BeaconMessageFormType) => void;
  handleSubmit: (beaconMessage: BeaconMessageFormType) => void;
  backToList: () => void;
  resetRedirectPath: () => void;
  // onChannelGroupSettingChange: (
  //   channelGroupSetting: ChannelGroupSetting
  // ) => void;
};

const channelGroupManager: ChannelGroupManager =
  new DefaultChannelGroupManager();
const beaconMessageManager: BeaconMessageManager =
  new DefaultBeaconMessageManager();
const advertiserManager: AdvertiserManager = new DefaultAdvertiserManager();
const adRequestSourceManager: AdRequestSourceManager =
  new DefaultAdRequestSourceManager();
export const useCreateBeaconMessageSetupFlowPageModel =
  (): BeaconMessageSetupFlowModel => {
    const { loading, callAPIs } = useCallAPI();
    const [redirectPath, setRedirectPath] = useState<string | undefined>();
    const [beaconConnectionOptions, setBeaconConnectionOptions] = useState<
      BeaconConnectionOption[]
    >([]);
    // const [channelGroupSetting, setChannelGroupSetting] =
    //   useState<ChannelGroupSetting>();
    const initBeaconMessage: BeaconMessageFormType = useMemo(() => {
      const startDate = moment().startOf('hour');
      const endDate = moment().add(1, 'day').endOf('day');
      const dateFormat = 'YYYY-MM-DD HH:mm:ss';
      return {
        name: '',
        channel: '',
        channelId: 0,
        adTypeId: 0,
        startDate: startDate.format(dateFormat),
        endDate: endDate.format(dateFormat),
        limitations: {
          include: [],
          exclude: [],
          preferred: [],
          nonPreferred: [],
          other: []
        },
        creative: {
          name: '',
          creativeType: 0,
          typeProperties: {
            stores: [],
            maxFrequency: 1,
            intervalHours: 5,
            beaconType: BeaconType.LINE_SIMPLE_BEACON
          },
          lineMessage: [
            {
              type: LineMessageType.TEXT,
              message: ''
            } as LineMessageFormData
          ]
        }
      };
    }, []);

    const [beaconMessage, setBeaconMessage] = useState<BeaconMessageFormType>();
    const [defaultBeaconMessage, setDefaultBeaconMessage] =
      useState<BeaconMessageFormType>();
    const [availableChannels, setAvailabelChannels] = useState<SelectOptions[]>(
      []
    );

    // const { loading: limitationLoading, limitationModel } = useLimitationModel(
    //   channelGroupSetting,
    //   beaconMessage?.limitations
    // );

    useEffect(() => {
      callAPIs(
        [
          () => channelGroupManager.getAllowedSalesChannels(),
          () => beaconMessageManager.getBeaconConnectionOptions()
        ],
        (
          availableChannels: SelectOptions[],
          beaconConnectionOptions: BeaconConnectionOption[]
        ) => {
          if (availableChannels.length > 0) {
            setAvailabelChannels(availableChannels);
            const channelId = +availableChannels[0].value;
            const channelGroupSetting = ChannelGroupSetting.fromId(channelId);
            // setChannelGroupSetting(channelGroupSetting);
            const adTypeSetting =
              channelGroupSetting.getActiveCampaignAdTypes()[0];
            const defaultBeaconMessage = {
              ...initBeaconMessage,
              channel: channelGroupSetting.key,
              channelId,
              adTypeId: adTypeSetting.adTypeId || 0,
              creative: {
                ...initBeaconMessage.creative,
                creativeType: adTypeSetting.bannerTypes[0].bannerTypeId,
                typeProperties: {
                  ...initBeaconMessage.creative.typeProperties,
                  beaconConnection: beaconConnectionOptions[0]
                }
              }
            };
            setBeaconMessage(defaultBeaconMessage);
            setDefaultBeaconMessage(cloneDeep(defaultBeaconMessage));
          }
          setBeaconConnectionOptions(beaconConnectionOptions);
        }
      );
    }, [initBeaconMessage, callAPIs]);

    const backToList = useCallback(() => {
      setRedirectPath('/beacon-messages');
    }, []);

    const resetRedirectPath = useCallback(() => {
      setRedirectPath(undefined);
    }, []);

    const handleSubmit = useCallback(
      (beaconMessage: BeaconMessageFormType) => {
        callAPIs(
          [() => beaconMessageManager.createBeaconMessage(beaconMessage)],
          () => {
            backToList();
          }
        );
      },
      [backToList, callAPIs]
    );

    return {
      type: 'create',
      beaconMessage,
      title: i18n.t('beaconMessageSetupFlowPage.titles.create'),
      loading,
      // loading: loading || limitationLoading,
      redirectPath,
      readonly: false,
      beaconConnectionOptions,
      defaultBeaconMessage,
      // limitationModel,
      availableChannels,
      setBeaconMessage,
      backToList,
      resetRedirectPath,
      handleSubmit
      // onChannelGroupSettingChange: setChannelGroupSetting
    };
  };

export const useEditBeaconMessageSetupFlowPageModel =
  (): BeaconMessageSetupFlowModel => {
    const { loading, callAPIs } = useCallAPI();
    const [redirectPath, setRedirectPath] = useState<string | undefined>();
    const { beaconMessageId } = useParams<{ beaconMessageId: string }>();
    const [beaconMessage, setBeaconMessage] = useState<
      BeaconMessageFormType | undefined
    >();
    const [defaultBeaconMessage, setDefaultBeaconMessage] = useState<
      BeaconMessageFormType | undefined
    >();
    // const [channelGroupSetting, setChannelGroupSetting] =
    //   useState<ChannelGroupSetting>();
    const [beaconConnectionOptions, setBeaconConnectionOptions] = useState<
      BeaconConnectionOption[]
    >([]);
    // const { loading: limitationLoading, limitationModel } = useLimitationModel(
    //   channelGroupSetting,
    //   beaconMessage?.limitations
    // );

    useEffect(() => {
      callAPIs(
        [
          () => beaconMessageManager.getBeaconConnectionOptions(),
          () => beaconMessageManager.getBeaconMessage(beaconMessageId)
        ],
        (
          beaconConnectionOptions: BeaconConnectionOption[],
          beaconMessage: BeaconMessageFormType
        ) => {
          setBeaconConnectionOptions(beaconConnectionOptions);
          setBeaconMessage(beaconMessage);
          setDefaultBeaconMessage(cloneDeep(beaconMessage));
          // setChannelGroupSetting(
          //   ChannelGroupSetting.fromId(beaconMessage.channelId)
          // );
        }
      );
    }, [beaconMessageId, callAPIs]);

    const backToList = useCallback(() => {
      setRedirectPath('/beacon-messages');
    }, []);

    const resetRedirectPath = useCallback(() => {
      setRedirectPath(undefined);
    }, []);

    const handleSubmit = useCallback(
      (beaconMessage: BeaconMessageFormType) => {
        callAPIs(
          [
            () =>
              beaconMessageManager.updateBeaconMessage(
                beaconMessageId,
                beaconMessage
              )
          ],
          () => {
            backToList();
          }
        );
      },
      [beaconMessageId, backToList, callAPIs]
    );

    return {
      type: 'edit',
      beaconMessage,
      defaultBeaconMessage,
      title: i18n.t('beaconMessageSetupFlowPage.titles.edit'),
      loading,
      // loading: loading || limitationLoading,
      redirectPath,
      readonly: false,
      beaconConnectionOptions,
      // limitationModel,
      availableChannels: undefined,
      setBeaconMessage,
      backToList,
      resetRedirectPath,
      handleSubmit
      // onChannelGroupSettingChange: setChannelGroupSetting
    };
  };

export const useSummaryBeaconMessageSetupFlowPageModel =
  (): BeaconMessageSetupFlowModel => {
    const { loading, callAPIs } = useCallAPI();
    const [redirectPath, setRedirectPath] = useState<string | undefined>();
    const { beaconMessageId } = useParams<{ beaconMessageId: string }>();
    const [beaconMessage, setBeaconMessage] = useState<
      BeaconMessageFormType | undefined
    >();
    const [defaultBeaconMessage, setDefaultBeaconMessage] = useState<
      BeaconMessageFormType | undefined
    >();
    const [beaconConnectionOptions, setBeaconConnectionOptions] = useState<
      BeaconConnectionOption[]
    >([]);

    useEffect(() => {
      callAPIs(
        [
          () => beaconMessageManager.getBeaconConnectionOptions(),
          () => beaconMessageManager.getBeaconMessage(beaconMessageId)
        ],
        (
          beaconConnectionOptions: BeaconConnectionOption[],
          beaconMessage: BeaconMessageFormType
        ) => {
          const beaconConnection =
            beaconMessage.creative.typeProperties.beaconConnection;
          // get coupon names & store names
          callAPIs(
            [
              () =>
                advertiserManager.getLineBeaconCoupons(
                  beaconConnection!.advertiserId,
                  beaconConnection!.value.toString()
                ),
              () =>
                adRequestSourceManager.getStoreNumOptions(
                  beaconMessage.channelId,
                  beaconConnection!.value
                )
            ],
            () => {
              setBeaconConnectionOptions(beaconConnectionOptions);
              setBeaconMessage(beaconMessage);
              setDefaultBeaconMessage(cloneDeep(beaconMessage));
            }
          );
        }
      );
    }, [beaconMessageId, callAPIs]);

    const backToList = useCallback(() => {
      setRedirectPath('/beacon-messages');
    }, []);

    const resetRedirectPath = useCallback(() => {
      setRedirectPath(undefined);
    }, []);

    const handleSubmit = useCallback(
      (_beaconMessage: BeaconMessageFormType) => {
        // no-op: summary mode does not support submit
      },
      []
    );

    return {
      type: 'summary',
      beaconMessage,
      defaultBeaconMessage,
      title: i18n.t('beaconMessageSetupFlowPage.titles.summary'),
      loading,
      redirectPath,
      readonly: true,
      beaconConnectionOptions,
      availableChannels: undefined,
      setBeaconMessage,
      backToList,
      resetRedirectPath,
      handleSubmit
    };
  };
